'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal' | 'bank';
  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  email?: string;
  accountNumber?: string;
  bankName?: string;
  isDefault: boolean;
}

const mockPaymentMethods: PaymentMethod[] = [
  {
    id: '1',
    type: 'card',
    cardNumber: '**** **** **** 4242',
    cardHolder: 'John Doe',
    expiryDate: '12/2027',
    isDefault: true,
  },
  {
    id: '2',
    type: 'paypal',
    email: 'john.doe@example.com',
    isDefault: false,
  },
];

export default function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(mockPaymentMethods);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleSetDefault = (id: string) => {
    setPaymentMethods(
      paymentMethods.map((method) => ({
        ...method,
        isDefault: method.id === id,
      }))
    );
  };

  const handleDelete = (id: string) => {
    setPaymentMethods(paymentMethods.filter((method) => method.id !== id));
  };

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'card':
        return 'CreditCardIcon';
      case 'paypal':
        return 'CurrencyDollarIcon';
      case 'bank':
        return 'BuildingLibraryIcon';
      default:
        return 'CreditCardIcon';
    }
  };

  const getPaymentLabel = (type: string) => {
    switch (type) {
      case 'card':
        return 'Credit/Debit Card';
      case 'paypal':
        return 'PayPal';
      case 'bank':
        return 'Bank Account';
      default:
        return 'Payment Method';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Payment Methods ({paymentMethods.length})
        </h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Icon name="PlusIcon" size={16} />
          Add Payment Method
        </button>
      </div>

      {/* Payment Methods Grid */}
      {paymentMethods.length > 0 ? (
        <div className="grid gap-6">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon name={getPaymentIcon(method.type)} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{getPaymentLabel(method.type)}</h3>
                      {method.isDefault && (
                        <span className="px-2 py-1 bg-success/10 text-success text-xs font-semibold rounded-full">
                          Default
                        </span>
                      )}
                    </div>

                    {method.type === 'card' && (
                      <div className="space-y-1">
                        <p className="text-sm font-mono text-foreground">{method.cardNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {method.cardHolder} • Expires {method.expiryDate}
                        </p>
                      </div>
                    )}

                    {method.type === 'paypal' && (
                      <p className="text-sm text-muted-foreground">{method.email}</p>
                    )}

                    {method.type === 'bank' && (
                      <div className="space-y-1">
                        <p className="text-sm font-mono text-foreground">{method.accountNumber}</p>
                        <p className="text-sm text-muted-foreground">{method.bankName}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!method.isDefault && (
                    <button
                      onClick={() => handleSetDefault(method.id)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="Set as default"
                    >
                      <Icon name="StarIcon" size={20} className="text-muted-foreground hover:text-warning" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(method.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Delete payment method"
                  >
                    <Icon name="TrashIcon" size={20} className="text-muted-foreground hover:text-error" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl p-12 text-center border border-border">
          <Icon name="CreditCardIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No payment methods saved</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="PlusIcon" size={16} />
            Add Your First Payment Method
          </button>
        </div>
      )}

      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-xl border border-border animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-semibold text-foreground">Add Payment Method</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>

            <div className="space-y-4">
              <button className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left">
                <Icon name="CreditCardIcon" size={24} className="text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">Credit/Debit Card</h4>
                  <p className="text-xs text-muted-foreground">Visa, Mastercard, Amex</p>
                </div>
              </button>

              <button className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left">
                <Icon name="CurrencyDollarIcon" size={24} className="text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">PayPal</h4>
                  <p className="text-xs text-muted-foreground">Link your PayPal account</p>
                </div>
              </button>

              <button className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left">
                <Icon name="BuildingLibraryIcon" size={24} className="text-primary" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">Bank Account</h4>
                  <p className="text-xs text-muted-foreground">Direct bank transfer</p>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowAddModal(false)}
              className="w-full mt-6 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}